/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { offEvent, onEvent } from "@adapptio/lib-shared";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import { DROPDOWN_STYLE, IDropdownMenuItem } from "../../../../ui/components/DropdownMenu";
import { LayoutState_Column } from "../../../../ui/components/Layout/LayoutState";
import { useLayoutElement } from "../../../../ui/components/Layout/useLayoutElement";
import { ToolbarButton, ToolbarButtonProps } from "../../../../ui/components/Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../../../../ui/components/Toolbar/ToolbarCommandButton";
import { ToolbarSpacer } from "../../../../ui/components/Toolbar/ToolbarSpacer";
import { ENDPOINT_EDITOR_VIEW_MODE, EndpointEditorEditView } from "../EndpointEditorEditView";
import { useDeepCompareMemo } from "use-deep-compare";
import { useSimpleEvent } from "../../../../ui/hooks/useSimpleEvent";
import { FC_ANCHOR, FieldButton, FieldGroup } from "@adapptio/lib-editor-ui";

export interface IEndpointEditorMainToolbarProps {
	view: EndpointEditorEditView;
}

const viewModeMenuItems: IDropdownMenuItem[] = [
	{
		id: ENDPOINT_EDITOR_VIEW_MODE.DESIGN,
		label: "Design",
		icon: "mdi/pencil-ruler"
	},
	{
		id: ENDPOINT_EDITOR_VIEW_MODE.DEBUG,
		label: "Debug",
		icon: "mdi/bug"
	}
];

export const EndpointEditorEditMainToolbar: FC<IEndpointEditorMainToolbarProps> = ({ view }) => {
	const layout = view.getLayout();

	const model = view.getEndpointModel();

	const panelLeft = useLayoutElement<LayoutState_Column>(layout.getById("panelLeft"));
	const panelRight = useLayoutElement<LayoutState_Column>(layout.getById("panelRight"));

	const [ resourceInfo, setResourceInfo ] = useState(() => ({
		resource: view.getResource(),
		parentList: view.getResourceParentList(),
		isBeingSaved: view.isBeingSaved()
	}));

	const [ viewInfo, setViewInfo ] = useState(view.getViewInfo());

	const [ viewMode, setViewMode ] = useState(() => view.getMode());

	const saveAllowed = useDeepCompareMemo(() => view.canSave(), [ viewInfo, resourceInfo ]);

	const selectedViewModeMenuItem = useMemo(
		() => viewModeMenuItems.find((item) => item.id === viewMode),
		[ viewMode ]
	);

	const handleSelectViewMode = useCallback<ToolbarButtonProps["onMenuItemClick"]>((item) => {
		view.setMode(item.id as ENDPOINT_EDITOR_VIEW_MODE);
	}, []);

	useSimpleEvent(
		view.onModeChange,
		() => {
			setViewMode(view.getMode());
		},
		[]
	);

	const [ visible, setVisible ] = useState({
		undo: view.canUndo(),
		redo: view.canRedo()
	});

	useSimpleEvent(
		model.onRender,
		() => {
			setVisible({
				undo: view.canUndo(),
				redo: view.canRedo()
			});
		},
		[ model ]
	);

	useEffect(() => {
		const handleResourceInfoChange = () =>
			setResourceInfo({
				resource: view.getResource(),
				parentList: view.getResourceParentList(),
				isBeingSaved: view.isBeingSaved()
			});

		const handleViewInfoChange = () => setViewInfo(view.getViewInfo());

		onEvent(view.onResourceInfoChanged, handleResourceInfoChange);
		onEvent(view.onViewInfoChanged, handleViewInfoChange);
		return () => {
			offEvent(view.onResourceInfoChanged, handleResourceInfoChange);
			offEvent(view.onViewInfoChanged, handleViewInfoChange);
		};
	}, [ view ]);

	return (
		<>
			<ToolbarCommandButton
				icon="mdi/dock-left"
				active={panelLeft.isExpanded}
				commandId="endpointEditor.edit.toggleLeftPanel"
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/undo-variant"
				disabled={!visible.undo}
				commandId="endpointEditor.edit.undo"
			/>

			<ToolbarCommandButton
				icon="mdi/redo-variant"
				disabled={!visible.redo}
				commandId="endpointEditor.edit.redo"
			/>

			<ToolbarSpacer grow={1} />

			<ToolbarCommandButton icon="mdi/magnify" commandId="endpointEditor.edit.flowEditor.zoomReset" />

			<ToolbarCommandButton
				icon="mdi/minus-circle-outline"
				commandId="endpointEditor.edit.flowEditor.zoomOut"
			/>

			<ToolbarCommandButton
				icon="mdi/plus-circle-outline"
				commandId="endpointEditor.edit.flowEditor.zoomIn"
			/>

			<ToolbarCommandButton
				icon="mdi/magnify-scan"
				commandId="endpointEditor.edit.flowEditor.zoomFit"
			/>

			<ToolbarSpacer />

			<FieldGroup>
				{viewModeMenuItems.map((item, index) => (
					<FieldButton
						key={index}
						icon={item.icon}
						tooltip={item.label}
						selected={item.id === selectedViewModeMenuItem.id}
						value={item.id}
						onClick={() => handleSelectViewMode(item)}
					/>
				))}
			</FieldGroup>

			<ToolbarSpacer grow={1} />

			{/*<ToolbarNote className="edt-module-endpoint-editor-main-toolbar__saved">
			<DateTimeAgo
				date={resourceInfo.resource.metaData.modifiedAt}
				valueTerm={
					resourceInfo.isBeingSaved
						? termsEditorCommon.resourceInfo.saving
						: viewInfo.isModified
							? "Modified"
							: "Saved"
				}
				tooltipTerm={termsEditorCommon.resourceInfo.savedAtBy}
				tVars={{ by: resourceInfo.resource.metaData.modifiedBy }}
			/>
		</ToolbarNote>
			<ToolbarSpacer />*/}

			<ToolbarCommandButton
				icon="mdi/content-save"
				commandId="endpointEditor.edit.saveResource"
				disabled={!saveAllowed}
				tooltip="Save"
			/>

			<ToolbarButton icon="mdi/backup-restore" disabled />

			<ToolbarSpacer />

			{/* {viewModeMenuItems.map((item, index) => <ToolbarButton
			key={index}
			icon={item.icon}
			active={item.id === selectedViewModeMenuItem.id}
			selectedMenuItemId={selectedViewModeMenuItem.id}
			onClick={() => handleSelectViewMode(item)}
		/> )} */}

			<ToolbarCommandButton
				icon="mdi/menu"
				tooltip="More options"
				menuItems={[
					{
						id: "rename",
						label: "Rename",
						icon: "mdi/pencil",
						commandId: "resource.rename",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "move",
						label: "Move",
						icon: "mdi/folder-move",
						commandId: "resource.moveDialog.open",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "duplicate",
						label: "Duplicate",
						icon: "mdi/content-copy",
						commandId: "resource.duplicate",
						commandArgs: [ resourceInfo.resource?.id, true ]
					},
					{
						id: "history",
						label: "Revision History",
						icon: "mdi/history",
						commandId: "resource.openRevisionHistory",
						commandArgs: [ resourceInfo.resource?.id ],
						disabled: true
					},
					{
						id: "export",
						label: "Export to YAML",
						icon: "mdi/export",
						commandId: "resource.export",
						commandArgs: [ resourceInfo.resource?.id ]
					},
					{
						id: "delete",
						label: "Delete",
						icon: "mdi/delete",
						commandId: "resource.delete",
						commandArgs: [ resourceInfo.resource?.id ]
					}
				]}
				menuAnchor={FC_ANCHOR.RIGHT_BOTTOM}
				menuStyle={DROPDOWN_STYLE.COMPACT}
			/>

			<ToolbarSpacer />

			<ToolbarCommandButton
				icon="mdi/dock-right"
				active={panelRight.isExpanded}
				commandId="endpointEditor.edit.toggleRightPanel"
			/>
		</>
	);
};
